import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Trash2, Check, X, AlertCircle, Save } from "lucide-react";
import { VersionDiffViewer } from "./VersionDiffViewer";
import type { SocialLink } from "./SocialsList";

interface SocialEditFormProps {
  editingSocial: Partial<SocialLink>;
  setEditingSocial: React.Dispatch<React.SetStateAction<Partial<SocialLink> | null>>;
  onSave: () => void;
  onCancel: () => void;
  socials: SocialLink[];
  activeDraft?: any;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
  liveSocial?: any;
}

export function SocialEditForm({
  editingSocial,
  setEditingSocial,
  onSave,
  onCancel,
  socials,
  activeDraft,
  onSaveDraft,
  onDiscardDraft,
  liveSocial,
}: SocialEditFormProps) {
  const diffFields = [
    { key: "name", label: "Display Name" },
    { key: "url", label: "URL Link" },
    { key: "icon", label: "Icon Name" },
    { key: "color", label: "Hover styling" },
  ];

  return (
    <div className="p-5 border border-border/60 rounded-2xl bg-card/65 space-y-4 max-w-xl text-left">
      <h5 className="font-bold text-base text-brand-primary">{editingSocial.name ? "Edit Social Link" : "Add Social Link"}</h5>

      {/* Staging Actions Panel */}
      <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
          <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
          <span>
            {activeDraft
              ? "⚠️ Staged Staging Draft has unpublished modifications."
              : "✅ Working copy is synced to database."}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {activeDraft && onDiscardDraft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDiscardDraft}
              className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
            </Button>
          )}
          {onSaveDraft && (
            <Button
              type="button"
              size="sm"
              onClick={onSaveDraft}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
            >
              <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Label>Name (Unique Identifier / Display Name)</Label>
        <Input placeholder="e.g. Github" value={editingSocial.name || ""} onChange={(e) => setEditingSocial((p) => ({ ...p!, name: e.target.value }))} disabled={!!socials.find(s => s.name === editingSocial.name)} />
      </div>

      <div className="space-y-1">
        <Label>URL</Label>
        <Input placeholder="e.g. https://github.com/..." value={editingSocial.url || ""} onChange={(e) => setEditingSocial((p) => ({ ...p!, url: e.target.value }))} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Icon Name</Label>
          <Input placeholder="github, linkedin, twitter, mail, twitch, youtube, users..." value={editingSocial.icon || ""} onChange={(e) => setEditingSocial((p) => ({ ...p!, icon: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label>Tailwind Hover Colors</Label>
          <Input placeholder="e.g. hover:text-[#ff0050]" value={editingSocial.color || ""} onChange={(e) => setEditingSocial((p) => ({ ...p!, color: e.target.value }))} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button variant="outline" onClick={onCancel}><X className="h-4 w-4 mr-2" /> Cancel</Button>
        <Button onClick={onSave} className="bg-brand-primary text-white"><Check className="h-4 w-4 mr-2" /> Publish Live</Button>
      </div>

      {liveSocial && (
        <VersionDiffViewer
          live={liveSocial}
          draft={editingSocial}
          fieldsToCompare={diffFields}
        />
      )}
    </div>
  );
}

export default SocialEditForm;
